// ./src/pages/private/PlayListItemsPage/PlayListItemsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  //AppIcon,
  AppToolbar,
  ConfirmModal,
  EmptyState,
} from "@/components/generic";
import {
  PlayListItemFormModal,
  PlayListItemsTable,
  PlayListSelector,
} from "@/components/features";
import { usePlayListItems, usePlayLists } from "@/hooks";
import { PlayList, PlayListItem } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const PlayListItemsPage: React.FC<PageProps> = (props: PageProps) => {
  const navigate = useNavigate();
  const { playListId } = useParams<{ playListId: string }>();
  const { isAuthenticated } = useApp();

  // Get the current playlist info
  const { playLists, get: getPlayList } = usePlayLists({
    autoLoad: false,
    isAuthenticated,
  });

  // Get the selected playlist from the list
  const selectedPlayList = playLists.find((p) => p.id === playListId);

  // Use the playlist items hook for all data management
  const {
    // Data state
    playListItems,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingPlayListItemId,
    selectedPlayListItem,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    //setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedPlayListItem,

    // Error handling
    lastError,
    clearError,

    // Helper operations
    reset,
  } = usePlayListItems({
    autoLoad: false,
    isAuthenticated,
    pageSize: 20,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [playListItemToDelete, setPlayListItemToDelete] =
    useState<PlayListItem | null>(null);
  //const [searchValue, setSearchValue] = useState<string>("");

  // Load playlist details on mount
  useEffect(() => {
    if (playListId) {
      getPlayList(playListId);
    }
  }, [playListId]); // Only depend on playListId, not getPlayList

  // Effect to load playlist items when playListId changes
  useEffect(() => {
    if (playListId) {
      // Reset any existing data and load for new playlist
      reset();
      //setSearchValue("");
      const playListFilter = { playListId: [playListId] };
      list(
        playListFilter,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortBy,
        true
      );
    } else {
      // Clear items when no playlist selected
      reset();
    }
  }, [playListId]); // Only depend on playListId

  // Handle playlist selection change from selector
  const handlePlayListChange = useCallback(
    (
      selectedPlayListId: string | string[] | null,
      _?: PlayList | PlayList[]
    ) => {
      if (selectedPlayListId && selectedPlayListId !== playListId) {
        // Navigate to the new playlist's items page
        navigate(`/play-lists/${selectedPlayListId}/items`);
      }
    },
    [playListId, navigate]
  );

  // Handle back to playlists
  const handleBackToPlayLists = useCallback(() => {
    navigate("/play-lists");
  }, [navigate]);

  // Handle search input change
  // const handleSearchChange = useCallback(
  //   (value: string) => {
  //     setSearchValue(value);

  //     if (!playListId) return;

  //     // Apply search filter
  //     const newFilters: any = { playListId: [playListId] };
  //     if (value.trim()) {
  //       newFilters.searchKeyword = value.trim();
  //     }

  //     // Update filters and reload data
  //     setFilters(newFilters);
  //     list(newFilters, { page: 1, pageSize: 20 }, sortBy, true);
  //   },
  //   [playListId, sortBy, setFilters, list]
  // );

  // Handle add item button click
  const handleAddItem = useCallback(() => {
    setSelectedPlayListItem(null);
    setFormModalOpened(true);
  }, [setSelectedPlayListItem]);

  // Handle edit item
  const handleEditItem = useCallback(
    (item: PlayListItem) => {
      setSelectedPlayListItem(item);
      setFormModalOpened(true);
    },
    [setSelectedPlayListItem]
  );

  // Handle duplicate item
  const handleDuplicateItem = useCallback(
    (item: PlayListItem) => {
      setSelectedPlayListItem({ ...item, id: undefined });
      setFormModalOpened(true);
    },
    [setSelectedPlayListItem]
  );

  // Handle remove item button click
  const handleRemoveItemClick = useCallback((item: PlayListItem) => {
    setPlayListItemToDelete(item);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (playListItemToDelete?.id) {
      const success = await remove(playListItemToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setPlayListItemToDelete(null);
      }
    }
  }, [playListItemToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setPlayListItemToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedPlayListItem(null);
    clearError();
  }, [setSelectedPlayListItem, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (playListId) {
      refresh();
    }
  }, [playListId, refresh]);

  // Handle item creation
  const handleCreateItem = useCallback(
    async (itemData: any) => {
      const result = await create(itemData);
      if (result) {
        setFormModalOpened(false);
        setSelectedPlayListItem(null);
      }
      return result;
    },
    [create, setSelectedPlayListItem]
  );

  // Handle item update
  const handleUpdateItem = useCallback(
    async (itemId: string, itemData: any) => {
      const result = await update(itemId, itemData);
      if (result) {
        setFormModalOpened(false);
        setSelectedPlayListItem(null);
      }
      return result;
    },
    [update, setSelectedPlayListItem]
  );

  // Handle order change via drag and drop
  const handleOrderChange = useCallback(
    async (
      draggedItem: PlayListItem,
      itemBefore: PlayListItem | undefined,
      itemAfter: PlayListItem | undefined
    ) => {
      if (!draggedItem.id) return;

      // Calculate new orderNo based on before and after items
      let newOrderNo: number;

      if (itemBefore && itemAfter) {
        // Item is between two items
        const beforeOrderNo = itemBefore.orderNo || 0;
        const afterOrderNo = itemAfter.orderNo || 0;
        newOrderNo = beforeOrderNo + (afterOrderNo - beforeOrderNo) / 2;
      } else if (itemAfter) {
        // Item is first (no item before)
        const afterOrderNo = itemAfter.orderNo || 1000;
        newOrderNo = afterOrderNo / 2;
      } else if (itemBefore) {
        // Item is last (no item after)
        const beforeOrderNo = itemBefore.orderNo || 0;
        newOrderNo = beforeOrderNo + 1000;
      } else {
        // No items (shouldn't happen, but fallback)
        newOrderNo = 1000;
      }

      // Add timestamp as decimal to ensure uniqueness
      // Use modulo 1000 to get last 3 digits of timestamp, then divide by 1000 to make it decimal
      const timestampDecimal = (Date.now() % 1000) / 1000;
      const finalOrderNo = newOrderNo + timestampDecimal;

      // Update only the orderNo field
      await update(draggedItem.id, {
        orderNo: finalOrderNo,
      });
    },
    [update]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with back button, playlist selector, actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "navigation-and-selector",
              items: [
                <AppButton
                  key="back"
                  variant="secondary"
                  iconName="ArrowLeft"
                  onClick={handleBackToPlayLists}
                  disabled={isCreating || !!removingPlayListItemId || loading}
                />,
                <PlayListSelector
                  key="playlist-selector"
                  value={playListId || null}
                  onChange={handlePlayListChange}
                  placeholder="Select playlist"
                  style={{ minWidth: 250 }}
                  clearable={false}
                  required
                  disabled={isCreating || !!removingPlayListItemId || loading}
                />,
                <AppButton
                  key="add-item"
                  iconName="Plus"
                  onClick={handleAddItem}
                  disabled={
                    isCreating ||
                    !!removingPlayListItemId ||
                    loading ||
                    !playListId
                  }
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={
                    isCreating ||
                    !!removingPlayListItemId ||
                    loading ||
                    !playListId
                  }
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            // {
            //   id: "search",
            //   items: [
            //     <TextInput
            //       key="search"
            //       placeholder="Search items..."
            //       leftSection={<AppIcon iconName="Search" size={14} />}
            //       value={searchValue}
            //       onChange={(e) => handleSearchChange(e.target.value)}
            //       style={{ minWidth: 250 }}
            //       disabled={!playListId}
            //     />,
            //   ],
            //   align: "right",
            // },
          ]}
        />

        {/* No Playlist Selected Message */}
        {!playListId && (
          <EmptyState
            display={true}
            title="No playlist selected"
            description="Please select a playlist from the list or go back to playlists page"
            actionIcon="ArrowLeft"
            actionLabel="Back to Playlists"
            onAction={handleBackToPlayLists}
          />
        )}

        {/* Playlist items table - only show when playlist is selected */}
        {playListId && (
          <PlayListItemsTable
            playListItems={playListItems}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onLoadMore={loadMore}
            onEditPlayListItem={handleEditItem}
            onDuplicatePlayListItem={handleDuplicateItem}
            onRemovePlayListItem={handleRemoveItemClick}
            onOrderChange={handleOrderChange}
            removingPlayListItemId={removingPlayListItemId}
            size="md"
          />
        )}

        {/* Empty state when playlist selected but no items */}
        {playListId && (
          <EmptyState
            display={isEmpty && !loading}
            title="No items in this playlist yet"
            description="Start by adding your first item to this playlist"
            actionIcon="Plus"
            actionLabel="Add Item"
            onAction={handleAddItem}
          />
        )}
      </Stack>

      {/* Playlist item form modal for create/edit */}
      {playListId && (
        <PlayListItemFormModal
          opened={formModalOpened}
          onClose={handleFormModalClose}
          playListId={playListId}
          venueId={selectedPlayList?.accountId}
          playListItem={selectedPlayListItem}
          onCreatePlayListItem={handleCreateItem}
          onUpdatePlayListItem={handleUpdateItem}
          isCreating={isCreating}
          isUpdating={isUpdating}
          lastError={lastError}
          onClearError={clearError}
        />
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Playlist Item"
        message={`Are you sure you want to delete this item from the playlist?`}
        details="This action cannot be undone. The item will be permanently removed from the playlist."
        confirmInProgress={removingPlayListItemId === playListItemToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
